/** Edit on a CUSTOM process row (E3, spec 2026-09-18 D15, §6 Edit): the
 * author's whole document, from processDraftStore. The map on top is
 * read-only (wireframe decision 12) and follows the draft; below it the E3
 * step cards run in `document` mode — contract editable, no locked rows —
 * with an exit editor each, add / remove / move controls, and the fields,
 * seats and lanes panels. The Raw view is the same draft as text.
 * The save / validate / publish bar is WorkflowDraftBar, mounted by the page
 * so it stays in reach on every tab. */
import { Component, useMemo, useState, type ReactNode } from 'react'
import { Button, NativeSelect, Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui'
import { useProcessDraftStore, type RemoveStepRefusal } from '@/stores/processDraftStore'
import { projectProcess, type ProcessOverviewVM } from '@/utils/workflowOverview'
import type { ProcessExitJson, ProcessWorkflowDefinitionJson } from '@/types/workflowDefinition'
import { AddStepDialog } from './AddStepDialog'
import { ProcessDocumentPanels } from './ProcessDocumentPanels'
import { ProcessExitEditor } from './ProcessExitEditor'
import { ProcessMap } from '@/components/process/map/ProcessMap'
import { ProcessStepEditor } from './ProcessStepEditor'
import { RawDefinitionEditor } from './RawDefinitionEditor'
import { defaultExit } from './stepSkeleton'
import { ProcessEditorToolbar } from './ProcessEditorToolbar'
import { FIELDS_CARD, LANES_CARD, SEATS_CARD, cardAnchor, stepCard, type EditorCards } from './editorCards'
import { editedFromDiff } from './editedCards'
import { countLine } from './editorSummaries'

/** The Raw view can hold any mapping; the form needs the process shape. A
 * document the projector cannot read is a Raw-view job, not a crash. */
function tryProject(doc: ProcessWorkflowDefinitionJson): ProcessOverviewVM | null {
  const isMap = (v: unknown) => !!v && typeof v === 'object' && !Array.isArray(v)
  const d = doc as unknown as Record<string, unknown>
  if (d.kind !== 'process' || !['steps', 'fields', 'seats', 'lanes', 'record'].every((k) => isMap(d[k]))) return null
  if (!Array.isArray((d.record as Record<string, unknown>).outcomes)) return null
  try {
    return projectProcess(doc)
  } catch {
    return null
  }
}

const UNREADABLE = (
  <p role="alert" className="rounded-xl border border-border bg-card p-4 text-sm text-foreground">
    The form can't read this document — a section it needs is missing or misshapen. Fix it in the Raw
    view; the bar below names what the server finds.
  </p>
)

/** The shape check above cannot anticipate every mapping the Raw view can
 * hold (a `needs` that is a string, say). A card that throws on one becomes
 * the same notice, and the next change to the document tries the form
 * again. (Not a `key` on the document: that would remount every input, and
 * drop its focus, on every keystroke.) */
class FormBoundary extends Component<{ doc: unknown; children: ReactNode }, { failed: boolean }> {
  state = { failed: false }
  static getDerivedStateFromError() {
    return { failed: true }
  }
  componentDidUpdate(previous: { doc: unknown }) {
    if (this.state.failed && previous.doc !== this.props.doc) this.setState({ failed: false })
  }
  render() {
    return this.state.failed ? UNREADABLE : this.props.children
  }
}

function refusalText(refusal: RemoveStepRefusal, vm: ProcessOverviewVM): string {
  const parts: string[] = []
  if (refusal.blockedBy.length > 0) {
    const from = refusal.blockedBy.map((id) => {
      const s = vm.steps.find((step) => step.id === id)
      return s ? `${s.number} ${s.title}` : id
    })
    parts.push(`an exit of ${from.join(', ')} leads here`)
  }
  if (refusal.pinnedAs.includes('entry')) parts.push('it is where the process starts')
  if (refusal.pinnedAs.includes('revision.resume_at')) parts.push('a revision resumes here')
  return `Can't remove this step: ${parts.join('; ')}. Rewire that first.`
}

export function ProcessDocumentEditor({
  roles = [],
  view,
  onViewChange,
  cards,
}: {
  /** Role names to suggest on a seat; may be empty. */
  roles?: string[]
  /** SA-1060: the page's open state for every card (it owns the deep links). */
  cards: EditorCards
  /** Controlled by the page when a finding deep-links to a card. */
  view?: 'form' | 'raw'
  onViewChange?: (view: 'form' | 'raw') => void
}) {
  const store = useProcessDraftStore()
  // The ROW is a process (the page mounts this editor by the row's kind);
  // the document is whatever the author has typed — the Raw view must
  // survive a `kind:` line mid-edit, so nothing here narrows on it.
  const doc = store.doc as ProcessWorkflowDefinitionJson | null
  const [localView, setLocalView] = useState<'form' | 'raw'>('form')
  const [adding, setAdding] = useState(false)
  const [refused, setRefused] = useState<{ stepId: string; refusal: RemoveStepRefusal } | null>(null)
  const vm = useMemo(() => (doc ? tryProject(doc) : null), [doc])
  // SA-1060 (ruling Q3): the marks are the server's last validate diff.
  const edited = useMemo(() => editedFromDiff(store.diff), [store.diff])
  if (!doc) return null
  const activeView = view ?? localView
  const setView = (next: string) => {
    const v = next === 'raw' ? 'raw' : 'form'
    setLocalView(v)
    onViewChange?.(v)
  }
  const stepIds = Object.keys(doc.steps ?? {})
  const cardIds = vm ? [...vm.steps.map((s) => stepCard(s.id)), FIELDS_CARD, SEATS_CARD, LANES_CARD] : []
  const line = vm
    ? countLine({
        steps: vm.steps.length,
        seats: Object.keys(doc.seats ?? {}).length,
        fields: Object.keys(doc.fields ?? {}).length,
        lanes: Object.keys(doc.lanes ?? {}).length,
        edited: edited.size,
        reordered: store.diff?.steps_reordered ?? false,
      })
    : ''
  /** SA-1060: a deep link opens the card first, so the scroll never lands on a closed row. */
  const openAndScroll = (stepId: string) => {
    cards.open(stepCard(stepId))
    window.setTimeout(
      () => document.getElementById(cardAnchor(stepCard(stepId)))?.scrollIntoView?.({ behavior: 'smooth', block: 'start' }),
      0,
    )
  }

  function renameExit(stepId: string, from: string, to: string) {
    const step = doc!.steps[stepId]
    // Rebuilt in place so the exit keeps its position; a timeout that named
    // the old exit follows the rename.
    const exits = Object.fromEntries(
      Object.entries(step.exits).map(([name, exit]): [string, ProcessExitJson] => [name === from ? to : name, exit]),
    )
    store.setStep(stepId, {
      exits,
      ...(step.timeout?.exit === from ? { timeout: { ...step.timeout, exit: to } } : {}),
    })
  }

  return (
    <Tabs value={activeView} onValueChange={setView}>
      <TabsList>
        <TabsTrigger value="form">Form</TabsTrigger>
        <TabsTrigger value="raw">Raw</TabsTrigger>
      </TabsList>

      <TabsContent value="form" className="space-y-3">
        {!vm ? (
          UNREADABLE
        ) : (
          <FormBoundary doc={doc}>
            <div className="rounded-2xl border border-border bg-card p-4">
              <ProcessMap vm={vm} currentStep={null} legendHint={null} onSelect={openAndScroll} />
            </div>
            <div className="flex flex-wrap items-end justify-between gap-3">
              <label className="block space-y-1 text-xs font-medium text-foreground">
                <span>The process starts at</span>
                <NativeSelect aria-label="The process starts at" value={doc.entry} onChange={(e) => store.setEntry(e.target.value)}>
                  {!stepIds.includes(doc.entry) && <option value={doc.entry}>{doc.entry || '— choose —'}</option>}
                  {vm.steps.map((s) => (
                    <option key={s.id} value={s.id}>
                      {s.number} {s.title}
                    </option>
                  ))}
                </NativeSelect>
              </label>
              <Button size="sm" variant="outline" onClick={() => setAdding(true)}>
                Add step
              </Button>
            </div>
            <ProcessEditorToolbar line={line} onExpandAll={() => cards.openAll(cardIds)} onCollapseAll={() => cards.closeAll(cardIds)} />
            {vm.steps.map((s, i) => (
              <ProcessStepEditor
                key={s.id}
                step={s}
                base={doc}
                overlay={{}}
                mode="document"
                onParam={store.setStepPath}
                onDocumentChange={store.setStep}
                cards={cards}
                edited={edited.has(stepCard(s.id))}
                contract={
                  <ProcessExitEditor
                    stepId={s.id}
                    doc={doc}
                    onSet={(name, exit) => store.setExit(s.id, name, exit)}
                    onRename={(from, to) => renameExit(s.id, from, to)}
                    newExit={defaultExit(doc, s.id)}
                  />
                }
                actions={
                  <div className="flex shrink-0 items-center gap-1">
                    <Button size="sm" variant="ghost" aria-label={`Move ${s.title} up`} disabled={i === 0} onClick={() => store.moveStep(s.id, 'up')}>
                      ↑
                    </Button>
                    <Button size="sm" variant="ghost" aria-label={`Move ${s.title} down`} disabled={i === vm.steps.length - 1} onClick={() => store.moveStep(s.id, 'down')}>
                      ↓
                    </Button>
                    <Button
                      size="sm"
                      variant="ghost"
                      aria-label={`Remove ${s.title}`}
                      onClick={() => {
                        const refusal = store.removeStep(s.id)
                        const blocked = refusal.blockedBy.length > 0 || refusal.pinnedAs.length > 0
                        setRefused(blocked ? { stepId: s.id, refusal } : null)
                      }}
                    >
                      Remove
                    </Button>
                  </div>
                }
              >
                {refused?.stepId === s.id && (
                  <p role="alert" className="text-xs text-destructive">
                    {refusalText(refused.refusal, vm)}
                  </p>
                )}
              </ProcessStepEditor>
            ))}
            <ProcessDocumentPanels doc={doc} roles={roles} onField={store.setField} onSeat={store.setSeat} onLane={store.setLane} cards={cards} edited={edited} />
          </FormBoundary>
        )}
      </TabsContent>

      <TabsContent value="raw">
        <RawDefinitionEditor
          doc={doc as unknown as Record<string, unknown>}
          onChange={(next) => store.setRaw(next as unknown as ProcessWorkflowDefinitionJson)}
        />
      </TabsContent>

      {adding && vm && (
        <AddStepDialog doc={doc} onAdd={store.addStep} onClose={() => setAdding(false)} />
      )}
    </Tabs>
  )
}
